import { isBefore } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { shapeDate } from '@avangard/utils/data';
import {
    BaseEntity,
    Entity as DecribeEntity,
    Relation,
    RelationList,
} from '@avangard/utils/entity';

import { getLocaleForDateFns } from '@lib/i18n';
import { LapEntity } from '@modules/lap/entities';
import {
    StudentOnlineApplicationStep,
    StudentOnlineApplicationStatus,
} from '@modules/types/graphql';
import { StudentEntity } from './student-entity';
import { StudentOnlineApplicationResponsibleEntity } from './student-online-application-responsible.entity';
import { StudentOnlineApplicationRecreationEntity } from './student-online-application-recreation.entity';
import { SoaDocumentEntityList } from './soa-document.entity-list';

import type { StudentOnlineApplication } from '@modules/types/graphql';
import type { SoaDocumentEntity } from './soa-document.entity';

export type Entity = Partial<StudentOnlineApplication> | null;

type Deadlines = {
    interactive: Date | null;
    fill: Date | null;
    chat: Date | null;
};

type AgeLimits = {
    isAdult: boolean;
    isFutureAdult: boolean;
};

@DecribeEntity('StudentOnlineApplicationEntity')
class StudentOnlineApplicationEntity extends BaseEntity {
    id: string;
    step: StudentOnlineApplicationStep;
    status: StudentOnlineApplicationStatus;
    active: boolean;
    deadline: Deadlines;
    ageLimits: AgeLimits;

    @Relation(() => StudentEntity)
    student: StudentEntity;

    @Relation(() => StudentOnlineApplicationResponsibleEntity)
    responsible: StudentOnlineApplicationResponsibleEntity;

    @Relation(() => StudentOnlineApplicationRecreationEntity)
    recreation: StudentOnlineApplicationRecreationEntity;

    @Relation(() => LapEntity)
    nearestLap: LapEntity;

    @RelationList(() => SoaDocumentEntityList)
    documents: SoaDocumentEntity[];

    constructor(entity: Entity) {
        super(entity);

        this.id = entity?.id ?? '';
        this.step = entity?.step ?? StudentOnlineApplicationStep.introduction;
        this.status = entity?.status ?? StudentOnlineApplicationStatus.empty;
        this.active = entity?.active ?? false;

        this.ageLimits = {
            isAdult: entity?.ageLimits?.isAdult ?? false,
            isFutureAdult: entity?.ageLimits?.isFutureAdult ?? false,
        };

        this.deadline = {
            interactive: shapeDate(entity?.deadline?.interactive),
            fill: shapeDate(entity?.deadline?.fill),
            chat: shapeDate(entity?.deadline?.chat),
        };
    }

    isPassedIntroduction() {
        return this.step !== StudentOnlineApplicationStep.introduction;
    }

    isActive() {
        return this.active && this.status !== StudentOnlineApplicationStatus.empty;
    }

    isDeadlined() {
        if (!this.deadline.interactive) {
            return true;
        }

        return isBefore(this.deadline.interactive, Date.now());
    }

    isAvailable() {
        return this.isActive() && !this.isDeadlined();
    }

    isMinor() {
        return !this.ageLimits.isAdult && !this.ageLimits.isFutureAdult;
    }

    getDeadlineString() {
        if (!this.deadline.fill || !this.deadline.chat || this.isDeadlined()) {
            return '-';
        }

        const locale = getLocaleForDateFns();

        const soaDeadlineString = formatInTimeZone(
            this.deadline.fill,
            'Europe/Moscow',
            'dd.MM.yyyy - kk:mm',
            { locale },
        );
        const chatDeadlineString = formatInTimeZone(
            this.deadline.chat,
            'Europe/Moscow',
            'dd.MM.yyyy - kk:mm',
            { locale },
        );

        return `Анкету необходимо заполнить до <span>${soaDeadlineString} МСК</span>, <br /> но ответы на ваши вопросы вы сможете получить только до <span>${chatDeadlineString} МСК</span>`;
    }

    isFinalStatus() {
        return [
            StudentOnlineApplicationStatus.schoolApproved,
            StudentOnlineApplicationStatus.approved,
            StudentOnlineApplicationStatus.rejected,
        ].includes(this.status);
    }
}

export { StudentOnlineApplicationEntity };
